import { useState, useCallback, useRef } from 'react';
import { sendMessage } from '../services/chatApi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export function useChat(lang: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const sessionIdRef = useRef(generateSessionId());

  const send = useCallback(
    async (content: string) => {
      if (rateLimited || loading) return;

      setError(null);
      setLoading(true);

      const userMessage: Message = { role: 'user', content };
      setMessages((prev) => [...prev, userMessage]);

      try {
        const response = await sendMessage(content, sessionIdRef.current, lang);
        const assistantMessage: Message = { role: 'assistant', content: response.reply };
        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        if (err instanceof Error && err.message === 'RATE_LIMITED') {
          setRateLimited(true);
        }
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [lang, rateLimited, loading],
  );

  return { messages, loading, error, rateLimited, send };
}
